import { AxiosResponse } from "axios";
import { GenericService } from "./Generic.service";

export interface ScheduleTimeAvailableBody {
  pacienteId: string;
  empresaId: string;
  tipoConsulta?: string;
  profissionalNome?: string;
  perfilSaude?: string;
  unidadeAtendimentoId?: string;
  data?: string;
}

export interface AttendantUnit {
  id: string;
  nome: string;
  dias: ScheduleDay[];
}

export interface ScheduleDay {
  dia: string;
  horarios: ScheduleTime[];
}

export interface ScheduleTime {
  id: string;
  horario: string;
  duracao: number;
}

export interface ScheduleTimeAvailableData {
  id: string;
  foto?: string | null;
  nome: string;
  tipoProfissional: string;
  unidadesAtendimento: AttendantUnit[];
}

export type ScheduleConsultType =
  | "Clinica"
  | "Periodica"
  | "Preventiva"
  | "Todos";

export type UpdateStatus =
  | "Atendimento_Clinico"
  | "Aguardando_Triagem"
  | "Aguarando_Atendimento"
  | "Em_Atendimento";

export interface ScheduleTimePostBody {
  pacienteId: string;
  empresaId: string;
  horarioId: string;
  tipoConsulta: string;
}

export interface ScheduleTimePostValue {
  id: string;
  profissionalId: string;
  perfilSaude: string;
  unidadeAtendimentoId: string;
  dataInicio: string;
  dataFim: string;
  proporcionalizar: boolean;
  tiposConsulta: {
    tipoConsulta: string;
    proporcao: number;
  }[];
  toleranciaEmHoras: number;
  considerarSabados: boolean;
  considerarDomingos: boolean;
  inativo: boolean;
  turnos: {
    id: string;
    horaInicio: string;
    horaFim: string;
    duracaoEmMinutos: number;
    inativo: boolean;
    turnosDia: {
      id: string;
      dia: string;
      inativo: boolean;
      horarios: {
        id: string;
        tipoConsulta: string;
        horario: string;
        duracaoMinutos: number;
        inativo: boolean;
      }[];
    }[];
  }[];
}

export interface DetailSchedule {
  id: string;
  profissional: {
    id: string;
    foto?: string | null;
    nome: string;
    especialidade: string;
  };
  unidadeAtendimento: {
    id: string;
    nome: string;
    endereco: {
      logradouro: string;
      numero: string;
      complemento: string;
      bairro: string;
      cidade: string;
      estado: string;
      cep: string;
    };
  };
  tipoConsulta: {
    nome: string;
    orientacoes: string;
  };
  horario: string;
}

export class SchedulingService<H> extends GenericService<H> {
  readonly URI: string = "Agendamento";

  constructor() {
    super();
    this.setUri(this.URI);
  }

  public postScheduleTimeAvailable(body: ScheduleTimeAvailableBody) {
    return super.postByResource<AxiosResponse<ScheduleTimeAvailableData[]>>(
      body as any,
      "HorariosDisponiveis"
    );
  }

  public postSchedule(body: ScheduleTimePostBody) {
    return super.postByResource<AxiosResponse<ScheduleTimePostValue>>(
      body as any
    );
  }

  public async getConsults() {
    return await super.getByResource("Card", { PageSize: 3 });
  }

  public async getConsultsPacientId(pacienteId: string) {
    return await super.getByResource(`Card/${pacienteId}`, { PageSize: 100 });
  }

  public async getInfoPainelConsultas(body: any) {
    return await super.getByResource("Painel", body);
  }

  public async getAllAppointments(dataInicio: string, dataFim?: string) {
    return await super.getByResource("Card", {
      DataInicio: dataInicio,
      DataFim: dataFim,
      PageSize: 10000,
    });
  }

  public async getConsultByID(id: string) {
    return await super.getByResource<AxiosResponse<DetailSchedule>>(
      `${id}/Details`
    );
  }

  public async getSimplifiedSchedulingById(id: string) {
    return await super.getByResource<any>(`${id}`);
  }

  public async postInactiveByID(id: string) {
    return await super.patchByResource<any>({} as any, `${id}/Inactivate`);
  }

  public async postEncaixe(params: any) {
    return await super.postByResource(params, "Encaixe");
  }

  public async updateAttendanceStatus(agendamentoId: string, params: any) {
    return await super.putByResource(params, `${agendamentoId}/StatusPresenca`);
  }

  public async getNextSchedule(profissionalId: string, unitId: string) {
    return await super.getByResource('ProximaAgenda', { ProfissionalId: profissionalId, UnidadeAtendimentoId: unitId })
  }
}
